import { motion } from "framer-motion";
import { ShieldCheck, UserCheck, FileSignature, Truck, Check, ArrowRight, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { FadeIn } from "@/components/Reveal";

const FIELD_IMAGE =
  "https://images.unsplash.com/photo-1621905252507-b35492cc74b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwyfHxmaWVsZCUyMG9wZXJhdGlvbnMlMjBzZXJ2aWNlJTIwd29ya2VyfGVufDB8fHx8MTc5MDYyNTQ3MHww&ixlib=rb-4.1.0&q=85";

const SERVICES = [
  {
    icon: ShieldCheck,
    title: "Governance, Risk & Compliance Support",
    span: "lg:col-span-3",
    listCols: "sm:grid-cols-2",
    items: [
      "Business compliance support",
      "Governance and operational structure",
      "Risk identification",
      "Compliance readiness",
      "Policy development",
      "Standard Operating Procedure creation",
      "Document organization and review",
      "Internal process development",
    ],
  },
  {
    icon: FileSignature,
    title: "Notary & Document Services",
    span: "lg:col-span-3",
    listCols: "sm:grid-cols-2",
    items: [
      "General notary services",
      "Mobile notary",
      "Remote online notarization",
      "Loan signing services",
      "Power of Attorney document notarization",
      "Estate document notarization",
      "Business document notarization",
      "I-9 Authorized Representative services",
      "Apostille support",
    ],
    note: "TSM Group CLE does not provide legal advice.",
  },
  {
    icon: UserCheck,
    title: "Workforce & Employment Verification",
    span: "lg:col-span-3",
    listCols: "sm:grid-cols-2",
    items: [
      "I-9 verification support",
      "Authorized Representative services",
      "Employee verification services",
      "Workforce documentation support",
      "Background screening coordination",
      "Employment onboarding support",
    ],
    note: "Employers remain responsible for meeting applicable federal, state, and local employment requirements.",
  },
  {
    icon: Truck,
    title: "Mobile Business Services",
    span: "lg:col-span-3",
    listCols: "sm:grid-cols-2",
    items: [
      "Onsite employer services",
      "Mobile notary services",
      "Field inspections",
      "Document verification",
      "Business support appointments",
    ],
    note: "Mobile services may be available depending on service type and location.",
  },
];

const slug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const ItemList = ({ items, cols = "" }) => (
  <ul className={`grid gap-x-6 gap-y-2 ${cols}`}>
    {items.map((item) => (
      <li key={item} className="flex items-start gap-2.5 text-sm leading-snug text-white/75">
        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" strokeWidth={3} />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const ServiceCard = ({ service, index }) => {
  const Icon = service.icon;
  return (
    <motion.article
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.12 }}
      transition={{ duration: 0.65, delay: (index % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className={`group flex h-full flex-col rounded-lg border border-white/10 bg-white/[0.04] p-7 transition-all duration-300 hover:-translate-y-1 hover:border-gold/60 hover:bg-white/[0.06] ${service.span}`}
      data-testid={`service-card-${index + 1}`}
    >
      <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-sm border border-gold/40 text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-navy">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="text-2xl font-extrabold leading-tight text-white">{service.title}</h3>
      <div className="mt-5 flex-1">
        <ItemList items={service.items} cols={service.listCols} />
        {service.note && (
          <p className="mt-5 border-t border-white/10 pt-4 text-xs italic leading-relaxed text-gold/80">{service.note}</p>
        )}
        <a
          href={`/services/${slug(service.title)}`}
          className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-gold transition-colors hover:text-white"
          data-testid={`service-details-link-${index + 1}`}
        >
          <ArrowRight className="h-3.5 w-3.5" />
          View service details
        </a>
      </div>
    </motion.article>
  );
};

export const Services = () => (
  <section id="services" className="noise-dark relative bg-navydeep py-24 lg:py-32">
    <div className="container-tsm relative">
      <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
        <FadeIn className="lg:col-span-8">
          <p className="eyebrow">Additional Services</p>
          <h2 className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="services-headline">
            Hands-on services that <span className="text-gold">keep you ready.</span>
          </h2>
        </FadeIn>
        <FadeIn delay={0.15} className="lg:col-span-4">
          <p className="max-w-sm leading-relaxed text-white/60 lg:justify-self-end lg:text-right">
            Alongside the TSM4 suite, TSM Group CLE provides the documentation, verification, and mobile services
            that keep daily operations moving. Many services can be delivered onsite.
          </p>
        </FadeIn>
      </div>

      <div className="mt-14 grid gap-6 lg:grid-cols-6" data-testid="services-grid">
        {SERVICES.map((service, i) => (
          <ServiceCard key={service.title} service={service} index={i} />
        ))}

        <motion.article
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.12 }}
          transition={{ duration: 0.65, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
          className="group flex h-full flex-col overflow-hidden rounded-lg border border-teal/30 bg-navy transition-all duration-300 hover:-translate-y-1 hover:border-teal lg:col-span-6 lg:flex-row"
          data-testid="service-card-coming-soon"
        >
          <div className="flex flex-1 flex-col justify-center p-7 lg:p-9">
            <div className="mb-5 inline-flex w-fit items-center gap-2 rounded-full border border-dashed border-teal/60 px-4 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-teal">
              <Clock className="h-3.5 w-3.5" />
              Coming Soon — Part of TSM4
            </div>
            <h3 className="text-2xl font-extrabold leading-tight text-white lg:text-3xl">
              TSM Drug Testing &amp; TSM Fingerprinting
            </h3>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-white/65">
              Workforce drug testing and electronic fingerprinting services are in preparation and not yet
              operational. Join the interest list on each service page to be first to know when they launch.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Link to="/tsm4/drug-testing" data-testid="services-coming-soon-drug-testing" className="btn-outline-light !px-5 !py-2.5 text-xs">
                Drug Testing Interest List
              </Link>
              <Link to="/tsm4/fingerprinting" data-testid="services-coming-soon-fingerprinting" className="btn-outline-light !px-5 !py-2.5 text-xs">
                Fingerprinting Waitlist
              </Link>
            </div>
          </div>
          <div className="relative hidden min-h-[220px] flex-1 lg:block lg:max-w-[42%]">
            <img
              src={FIELD_IMAGE}
              alt="Field service professional equipped for an onsite business service appointment"
              className="absolute inset-0 h-full w-full object-cover"
              data-testid="services-mobile-image"
            />
            <div
              className="absolute inset-0 bg-gradient-to-r lg:from-navy lg:to-transparent lg:opacity-90"
              aria-hidden="true"
            />
          </div>
        </motion.article>
      </div>
    </div>
  </section>
);
